"use client";

import { useTranslation } from "react-i18next";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export interface BestTimeSlot {
  dayOfWeek: number; // 0 = Sun .. 6 = Sat, or 1..7 — we accept 0..6
  hour: number;
  score: number;
}

interface BestTimeHeatmapProps {
  slots: BestTimeSlot[];
  topSlot?: { dayOfWeek: number; hour: number; label?: string };
  platform?: string;
  onPlatformChange: (p?: string) => void;
  availablePlatforms: string[];
}

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const DAY_LABEL_ORDER = [1, 2, 3, 4, 5, 6, 0]; // Mon..Sun rows

function cellColor(score: number, max: number): string {
  if (max <= 0) return "hsl(210, 16%, 93%)"; // slate-100-ish
  const t = Math.max(0, Math.min(1, score / max));
  // interpolate from slate-100 (210,16%,93%) to emerald-500 (160,84%,39%)
  const h = 210 + (160 - 210) * t;
  const s = 16 + (84 - 16) * t;
  const l = 93 + (39 - 93) * t;
  return `hsl(${h}, ${s}%, ${l}%)`;
}

export function BestTimeHeatmap({
  slots,
  topSlot: _topSlot,
  platform,
  onPlatformChange,
  availablePlatforms,
}: BestTimeHeatmapProps) {
  const { t } = useTranslation("analytics");
  const DAYS_T = [
    t("day.sun"), t("day.mon"), t("day.tue"), t("day.wed"),
    t("day.thu"), t("day.fri"), t("day.sat"),
  ];
  const map = new Map<string, number>();
  let max = 0;
  for (const s of slots) {
    const key = `${s.dayOfWeek}:${s.hour}`;
    map.set(key, s.score);
    if (s.score > max) max = s.score;
  }

  const sortedTop = [...slots]
    .sort((a, b) => b.score - a.score)
    .slice(0, 3)
    .map((s) => `${s.dayOfWeek}:${s.hour}`);
  const topSet = new Set(sortedTop);

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-medium">{t("heatmap.title")}</h3>
        <Select
          value={platform ?? "all"}
          onValueChange={(v) => onPlatformChange(v === "all" ? undefined : v)}
        >
          <SelectTrigger className="h-8 w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("heatmap.allPlatforms")}</SelectItem>
            {availablePlatforms.map((p) => (
              <SelectItem key={p} value={p} className="capitalize">
                {p}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <TooltipProvider delayDuration={100}>
        <div className="overflow-x-auto">
          <div className="inline-block">
            {/* Hour header */}
            <div
              className="grid gap-0.5"
              style={{
                gridTemplateColumns: `36px repeat(24, minmax(14px, 1fr))`,
              }}
            >
              <div />
              {HOURS.map((h) => (
                <div
                  key={h}
                  className="text-center text-[9px] text-muted-foreground"
                >
                  {h % 3 === 0 ? h : ""}
                </div>
              ))}
            </div>
            {/* Rows */}
            {DAY_LABEL_ORDER.map((dow) => (
              <div
                key={dow}
                className="mt-0.5 grid gap-0.5"
                style={{
                  gridTemplateColumns: `36px repeat(24, minmax(14px, 1fr))`,
                }}
              >
                <div className="flex items-center text-[10px] text-muted-foreground">
                  {DAYS_T[dow]}
                </div>
                {HOURS.map((h) => {
                  const key = `${dow}:${h}`;
                  const score = map.get(key) ?? 0;
                  const isTop = topSet.has(key) && score > 0;
                  return (
                    <Tooltip key={h}>
                      <TooltipTrigger asChild>
                        <div
                          className="relative h-4 rounded-sm"
                          style={{ backgroundColor: cellColor(score, max) }}
                        >
                          {isTop && (
                            <span className="absolute inset-0 flex items-center justify-center">
                              <span className="h-1 w-1 rounded-full bg-white shadow" />
                            </span>
                          )}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        {t("heatmap.cellTooltip", { day: DAYS_T[dow], hour: h, score: score.toFixed(0) })}
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </TooltipProvider>
    </div>
  );
}
